import { cn } from "@/lib/utils";
import { useTopics } from "@/hooks/useTopics";
import { useArticlesGroupedByTopic } from "@/hooks/useArticlesByTopic";
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';

interface LearnTopicNavProps {
  activeTopic: string;
  onTopicChange: (topic: string) => void;
  visible?: boolean;
}

export default function LearnTopicNav({
  activeTopic,
  onTopicChange,
  visible,
}: LearnTopicNavProps) {
  const { data: topics, isLoading: topicsLoading } = useTopics();
  const { data: articlesGrouped, isLoading: articlesLoading } = useArticlesGroupedByTopic();
  
  // Filter to only active topics that have at least one published article
  const topicsWithArticles = (topics?.filter(t => t.is_active) || []).filter(topic => {
    const articles = articlesGrouped?.[topic.id];
    return articles && articles.length > 0;
  });
  
  // Build the full list: "All" + only topics that have articles
  const topicNames = ["All", ...topicsWithArticles.map(t => t.name)];
  
  const isLoading = topicsLoading || articlesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center gap-8">
        <span className={cn(
          "text-xs animate-pulse",
          visible ? "text-foreground" : "text-white"
        )}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-8">
      {topicNames.map((topic) => (
        <button
          key={topic}
          onClick={() => {
            // ⚠️ ANALYTICS: Track topic filter
            trackEvent(ANALYTICS_EVENTS.TOPIC_FILTER_CLICKED, {
              nav_type: 'topic_filter',
              nav_item_label: topic,
              nav_page: '/learn',
              previous_topic: activeTopic,
              new_topic: topic,
              topic_position: topicNames.indexOf(topic),
              total_topics: topicNames.length,
            });
            onTopicChange(topic);
          }}
          className={cn(
            "text-xs transition-colors duration-300 hover:opacity-70",
            visible ? "text-foreground" : "text-white",
            activeTopic === topic ? "font-bold" : "font-normal"
          )}
        >
          {topic}
        </button>
      ))}
    </div>
  );
}
